/**
 * Typed client for WebSocket /ws/client (§6.2).
 * - cookie auth (no token in the URL), same origin;
 * - ping every 15 s;
 * - automatic reconnection with backoff 1, 2, 4, 8, 16, 30 s (+-20% jitter);
 * - no reconnection after `kick` or close 4401 (the app decides what to do).
 */
import { CLIENT_CLOSE, PING_INTERVAL_MS, type ClientToServer, type KickReason, type ServerToClient } from './protocol';
import { parseServerMessage } from './validate';
import { backgroundTimeout, startBackgroundTicker, type Ticker, type TimeoutHandle } from '../spatial/backgroundTicker';

export type SocketStatus = 'idle' | 'connecting' | 'open' | 'reconnecting' | 'stopped';

export interface SocketCloseInfo {
  code: number;
  reason: string;
  /** True when no `hello` was received on this connection (possible auth failure). */
  beforeHello: boolean;
}

export interface ClientSocketHandlers {
  onMessage(msg: ServerToClient): void;
  onStatus?(status: SocketStatus): void;
  onClose?(info: SocketCloseInfo): void;
  /** `kick` received: the socket will not reconnect by itself. */
  onKick?(reason: KickReason): void;
  /** Close 4401 (unauthenticated): the socket will not reconnect. */
  onUnauthorized?(): void;
}

export interface SocketLike {
  readyState: number;
  onopen: ((ev: Event) => void) | null;
  onclose: ((ev: CloseEvent) => void) | null;
  onmessage: ((ev: MessageEvent) => void) | null;
  onerror: ((ev: Event) => void) | null;
  send(data: string): void;
  close(code?: number, reason?: string): void;
}

export interface ClientSocketOptions {
  url?: string;
  createSocket?: (url: string) => SocketLike;
  random?: () => number;
}

const BACKOFF_S = [1, 2, 4, 8, 16, 30];

export function backoffDelayMs(attempt: number, random: () => number = Math.random): number {
  const base = BACKOFF_S[Math.min(attempt, BACKOFF_S.length - 1)]! * 1000;
  const jitter = 1 + (random() * 0.4 - 0.2);
  return Math.round(base * jitter);
}

export function defaultSocketUrl(): string {
  const { protocol, host } = window.location;
  return `${protocol === 'https:' ? 'wss:' : 'ws:'}//${host}/ws/client`;
}

export class ClientSocket {
  private ws: SocketLike | null = null;
  private status: SocketStatus = 'idle';
  private attempt = 0;
  private pingN = 0;
  // Timer nel worker: con la finestra ridotta a icona Chrome ritarderebbe la riconnessione fino a 1 minuto.
  private pingTimer: Ticker | null = null;
  private retryTimer: TimeoutHandle | null = null;
  private gotHello = false;
  private stopped = false;
  private readonly createSocket: (url: string) => SocketLike;
  private readonly random: () => number;
  private url: string | undefined;

  constructor(
    private readonly handlers: ClientSocketHandlers,
    opts: ClientSocketOptions = {},
  ) {
    this.url = opts.url;
    this.createSocket = opts.createSocket ?? ((url) => new WebSocket(url) as unknown as SocketLike);
    this.random = opts.random ?? Math.random;
  }

  getStatus(): SocketStatus {
    return this.status;
  }

  start(): void {
    this.stopped = false;
    this.attempt = 0;
    this.clearRetry();
    this.open();
  }

  stop(): void {
    this.stopped = true;
    this.clearRetry();
    this.stopPing();
    const ws = this.ws;
    this.ws = null;
    if (ws) {
      ws.onclose = null;
      ws.onmessage = null;
      ws.close(1000, 'bye');
    }
    this.setStatus('stopped');
  }

  /** Sends a message if the socket is open. Returns false otherwise (messages are not queued). */
  send(msg: ClientToServer): boolean {
    if (!this.ws || this.ws.readyState !== 1) return false;
    this.ws.send(JSON.stringify(msg));
    return true;
  }

  private open(): void {
    this.setStatus(this.attempt === 0 ? 'connecting' : 'reconnecting');
    this.gotHello = false;
    const ws = this.createSocket(this.url ?? defaultSocketUrl());
    this.ws = ws;
    ws.onopen = () => {
      this.setStatus('open');
      this.startPing();
    };
    ws.onmessage = (ev) => {
      const msg = typeof ev.data === 'string' ? parseServerMessage(ev.data) : null;
      if (!msg) return;
      if (msg.type === 'hello') {
        this.gotHello = true;
        this.attempt = 0;
      }
      if (msg.type === 'kick') {
        this.stopped = true;
        this.handlers.onKick?.(msg.reason);
      }
      this.handlers.onMessage(msg);
    };
    ws.onerror = () => {
      /* onclose follows */
    };
    ws.onclose = (ev) => {
      if (this.ws !== ws) return;
      this.ws = null;
      this.stopPing();
      const info: SocketCloseInfo = { code: ev.code, reason: ev.reason, beforeHello: !this.gotHello };
      this.handlers.onClose?.(info);
      if (ev.code === CLIENT_CLOSE.UNAUTHORIZED) {
        this.stopped = true;
        this.handlers.onUnauthorized?.();
      }
      if (this.stopped) {
        this.setStatus('stopped');
        return;
      }
      this.scheduleReconnect();
    };
  }

  private scheduleReconnect(): void {
    this.setStatus('reconnecting');
    const delay = backoffDelayMs(this.attempt, this.random);
    this.attempt += 1;
    this.clearRetry();
    this.retryTimer = backgroundTimeout(delay, () => {
      this.retryTimer = null;
      if (!this.stopped) this.open();
    });
  }

  private startPing(): void {
    this.stopPing();
    this.pingTimer = startBackgroundTicker(PING_INTERVAL_MS, () => {
      this.pingN += 1;
      this.send({ type: 'ping', n: this.pingN });
    });
  }

  private stopPing(): void {
    this.pingTimer?.stop();
    this.pingTimer = null;
  }

  private clearRetry(): void {
    this.retryTimer?.cancel();
    this.retryTimer = null;
  }

  private setStatus(s: SocketStatus): void {
    if (this.status === s) return;
    this.status = s;
    this.handlers.onStatus?.(s);
  }
}
